import { matchVendorDriver, type Change, type RacePlanDoc, type ResolvedDriver } from '@racedash/shared'
import type { EventFuelContext } from './useFuelBurn'

/**
 * What a screen needs to know about *whose* dashboard it is on.
 *
 * Separate from DashboardData, which is the timing — facts about a public race
 * feed, identical for everybody looking. This is the organisation's own layer:
 * which cars are theirs, who they say is driving, and whether this viewer may
 * change any of it.
 *
 * Bundled rather than passed as six props because it threads through every
 * screen and most screens use none of it.
 */
export interface ScreenContext {
  /**
   * Stable key for settings kept in this browser.
   *
   * Screen ids are only unique within a dashboard — "stints-1" exists on every
   * dashboard that has a stint screen — so the dashboard id has to be part of
   * it or two dashboards share one filter.
   */
  viewId: string
  /** Car numbers this organisation has entered at the event being shown. */
  ourCars: string[]
  /** The organisation's drivers, for declaring who is in a car. */
  drivers: { id: string; name: string; shortName?: string | null }[]
  /**
   * Which of those drivers are entered in which car, by car number.
   *
   * A club roster is everybody who has ever raced for the team; the entry names
   * the handful actually in the car this weekend. Anywhere a driver is picked
   * for a known car, that shorter list is the right one — picking from four is
   * a glance, picking from thirty is a search.
   *
   * Absent, or empty for a car, means "we were not told", and the caller falls
   * back to the whole roster rather than offering nothing.
   */
  entryDrivers?: Record<string, string[]>
  /** What the organisation has already said about who was in which car. */
  declarations: Change[]
  /**
   * Records that a driver got into a car at a moment.
   *
   * Absent for anyone who may not edit — a viewer, or a share-link holder, who
   * has no organisation at all. Screens test for it rather than being told
   * separately whether editing is allowed, so an editable control cannot be
   * rendered without something behind it.
   */
  declare?: (car: string, driverId: string | null, from: Date) => Promise<void>
  /**
   * The team's plan for whatever session is on screen, where one has been made.
   *
   * Here rather than in DashboardData because it is the organisation's own
   * decision, not a fact about the race — the same reason declarations are.
   */
  plan?: RacePlanDoc | null
  /** When the session on screen was scheduled to start and end, for windows
   *  anchored to either. */
  sessionStart?: Date | null
  sessionEnd?: Date | null
  /**
   * The circuit's length, which gives a neutralised lap time before the session
   * has produced one of its own. Only five of the catalogue's circuits carry it
   * so far, so its absence is ordinary and falls back to a multiple of the
   * green lap.
   */
  trackLengthMetres?: number | null
  /**
   * The event's fuel: the team's sessions there and what it has said about its
   * fills. A tank spans sessions, so burn is worked out across the event
   * rather than from the session on screen. Absent on a share link, which
   * has no team to say which sessions are its own.
   */
  fuel?: EventFuelContext
}

/** For the public share view and the sample preview: timing, and nothing else. */
export const noContext = (viewId: string, ourCars: string[] = []): ScreenContext => ({
  viewId,
  ourCars,
  drivers: [],
  declarations: [],
})

/**
 * The drivers entered in a car, or the whole roster where the entry says
 * nothing.
 *
 * One rule in one place. It had been copied into the stop prompt, the stint
 * chart and the planner, and a fourth variant into the plan editor — and the
 * fallback is the half that matters: an empty picker is worse than a long one.
 */
export function enteredDrivers(
  context: ScreenContext,
  car: string | null,
): ScreenContext['drivers'] {
  const entered = car ? context.entryDrivers?.[car] : undefined
  if (!entered || entered.length === 0) return context.drivers
  const narrowed = context.drivers.filter((d) => entered.includes(d.id))
  return narrowed.length > 0 ? narrowed : context.drivers
}

/** A driver's name from the whole roster, or null. */
export function driverName(context: ScreenContext, id: string | null | undefined): string | null {
  return id ? (context.drivers.find((d) => d.id === id)?.name ?? null) : null
}

/**
 * The roster driver a vendor's name refers to, if it can be pinned to one.
 *
 * Tried against the car's entry first, where "T.QUA" has three people to be
 * rather than thirty, and only then the whole roster.
 */
export function rosterDriverFor(
  context: ScreenContext,
  car: string | null,
  vendorName: string | null | undefined,
): string | null {
  return (
    matchVendorDriver(vendorName, enteredDrivers(context, car)) ??
    matchVendorDriver(vendorName, context.drivers)
  )
}

/**
 * What to print for a resolved driver, and who that is on the roster.
 *
 * One answer for every screen that names a driver, so the tile, the stint
 * chart and the lap table cannot show the same person three ways. A declared
 * driver is the roster's name; a vendor name is the roster's name where it
 * matches one and the vendor's spelling where it does not; nobody is null.
 */
export function describeDriver(
  context: ScreenContext,
  car: string | null,
  resolved: ResolvedDriver,
): { name: string | null; driverId: string | null; source: ResolvedDriver['source'] } {
  if (resolved.source === 'team') {
    // A driver removed from the team list after being declared. The declaration
    // is still a fact about the race, so it is shown as one rather than
    // disappearing.
    return {
      name: driverName(context, resolved.driverId) ?? 'Unknown driver',
      driverId: resolved.driverId,
      source: 'team',
    }
  }
  if (resolved.source === 'vendor') {
    const id = rosterDriverFor(context, car, resolved.name)
    return { name: (id && driverName(context, id)) || resolved.name, driverId: id, source: 'vendor' }
  }
  return { name: null, driverId: null, source: 'none' }
}
